import { Injectable } from '@angular/core';
import { shareReplay, switchMap, map } from 'rxjs/operators';
import {
  PatientClient,
  SymptomQuestion,
  QuestionBase,
  PatientFilterCriteria,
  EncounterPlanOutcomeVo,
  AllergenVo,
} from './api-client.service';
import { AuthQuery } from './state/auth/auth.query';
import { HttpClient } from '@angular/common/http';
import { AlgoliaRestService } from './algolia-rest.service';
import { LimitAvailable } from './view-models/patient-financials-view-model';
import { combineLatest, Observable } from 'rxjs';
import { VERSION } from '@env/version';
import { GenericMedicine } from './view-models/generic-medicine-view-model.utils';
import * as _ from 'lodash';
import { MerakiLookupService } from './meraki/meraki-lookup.service';
import { ChartDefaultData } from '@app/modules/patient/patient-vitals/vitals-growth-chart/utils/growth-chart-render-util';
import { Questionnaire } from './meraki-models/questionnaires.models';
import * as moment from 'moment';

export const ProviderOptionEnabledForHBClinical = 'IsHealthbridgeClinicalEnabled';
export const ProviderOptionEnabledForHBClinicalLite = 'IsHealthbridgeClinicalLiteEnabled';

export const PatientIdentityNoRegex = /^([0-9][0-9])([0][1-9]|[1][0-2])(0[1-9]|[12][0-9]|3[01])([0-9])([0-9]{3})([0|1])([0-9])([0-9])$/;
export const AccountNoRegex = /^[a-zA-Z0-9\\W\s]+[\\s_/\\-a-zA-Z0-9]*$/;
export const ContactNoRegex = /^[0-9]*$/;
export const MedDepCodeRegex = /^[0-9]*$/;

// Modifiers that require admin processing and cannot be priced
export const ADMIN_MODIFIER_CODES = ['0017'];

export const WaitingRoomStatusEnum: { [key: number]: string } = {
  0: 'Scheduled',
  1: 'Checked In',
  2: 'In Progress',
  3: 'Completed',
  4: 'Canceled',
  5: 'Deleted',
};

export interface MyPatientFilesCategories {
  patientCategories: string[];
  practiceCategories: string[];
}

export interface IClinicalMetricInfo {
  name: string;
  type: string;
  vital: boolean;
}

export interface ISurgery {
  specialities: string[] | null;
  specialitiesExcluded?: string[] | null;
  name: string;
  common: number | 0;
}

export interface Language {
  key: string;
  language: string;
}

export interface IClinicalMetricValue extends IClinicalMetricInfo {
  value: string;
  valueUnit: string;
  valueFlag: string;
  date: Date;
}

export interface ISeverityMetric {
  name: string;
  severity?: string[] | null;
}

export interface IMetricSchema {
  groupName: string;
  groupTitle: string;
  isCollapsible: boolean;
  isCollapsed: boolean;
  className: string;
  items: IMetricSchemaItem[];
}

export interface IMetricSchemaItem {
  vital?: boolean;
  type: string;
  uom: string;
  name: string;
  id: string;
  min: number;
  max: number;
  maxlength: number;
  mask: string;
  allowNone?: boolean; // Adds a None option with null value to dropdowns
  options: string[];
  required: boolean;
  validationMessage: any;
  value: string;
  oldValue: string;
  valueFlag: string;
  oldValueFlag: string;
  oldValueDate: Date;
  oldType: string;
  placeholder: string;
  step: number;
  checked: boolean;
  readonly?: boolean;
  gender?: string;
  usage?: string[];
  captured?: boolean;
  useOptionAsUOM?: boolean;
  visibleWhen?: {
    id?: string;
    equals?: any;
    all?: Array<{
      id: string;
      equals: any;
    }>;
  };
  softWarning?: {
    message: string;
    anyOf: Array<{
      greaterThan?: number;
      greaterThanOrEqual?: number;
      lessThan?: number;
      lessThanOrEqual?: number;
    }>;
  };
}

export interface PlaceOfService {
  name: string;
  code: string;
}

export enum EnounterLineType {
  Procedure = 'Procedure',
  Consumable = 'Consumable',
  Medicine = 'Medicine',
  Modifier = 'Modifier',
}

export enum BenefitCheckStatus {
  FullyCovered = 'Fully covered',
  PartiallyCovered = 'Partially covered',
  NotCovered = 'Not covered',
  Rejected = 'Rejected',
  AwaitingResponse = 'Awaiting response',
  // Rejected = 'Rejected',
}

export enum PatientGenericInformationCategories {
  Gynae = 'Gynae',
}

export interface Lifestyle {
  category: string;
  name: string;
  label: string;
  ordinal: number;
  control: Control;
  severity?: Control;
}

export interface Control {
  type: string;
  placeholder: string;
  options: string[];
}

export interface UiColor {
  Name: string;
  Hex: string;
  RGB?: string;
  ClassName: string;
}

export enum MetricUsages {
  all = '*',
  metric = 'metric',
  screening = 'screening',
  commonScreening = 'commonScreening',
  customMetrics = 'customMetrics',
}

export interface NavigationItem {
  id: string;
  title: string;
  url?: string;
  openInNewTab?: boolean;
  fragment?: string;
  type: 'item' | 'group';
  classes?: string;
  hidden?: boolean;
  componentRef?: any;
  function?: any;
  children?: NavigationItem[];
}

export interface SymptomConfiguration {
  SymptomId?: string | null;
  ExcludedConsultSections?: string[];
  ExcludedFromConsultSection?: string[];
  Filter?: PatientFilterCriteria;
  Container?: string;
}

export enum EncounterTypes {
  MedicalInsurance = 'MedicalInsurance',
  MedicalAidInvoice = 'MedicalAidInvoice',
  CashInvoice = 'CashInvoice',
  NoChargeInvoice = 'NoChargeInvoice',
}

export enum MedicineTypes {
  Prescribe = 'Prescribe',
  Dispense = 'Dispense',
  Consumable = 'Consumable',
}

export enum PlanOutcomeTypes {
  PathRequest = 'path',
  FollowUp = 'follow-up',
  Referred = 'referred',
  Ultrasound = 'ultrasound',
  Radiology = 'radiology',
  AdmitToHospital = 'hospital-admit',
  DischargeFromHospital = 'hospital-discharge',
  PrescriptionSent = 'prescription-sent',
}

export enum TaskType {
  AdmitToHospital = 'Admit to hospital',
  FollowUpVisit = 'Follow-up visit',
  PathologyResult = 'Pathology result',
  Prescriptions = 'Prescriptions',
  PrescriptionRenewal = 'Prescription renewal',
  Radiology = 'Radiology',
  ReferralLetter = 'Referral letter',
  SickNote = 'Sick note',
  TelephoneCall = 'Telephone call',
  Other = 'Other',
}

export enum ConsultationQuestionStore {
  Consultation = 'Consultation',
  PatientGenericInformation = 'PatientGenericInformation',
}

export enum CommunicationTemplateType {
  CustomLetter = 'custom-letter',
  PlanSMS = 'plan-sms',
  PathologySMS = 'pathology-sms',
}

@Injectable({
  providedIn: 'root',
})
export class ReferenceDataService {
  public readonly allergyCategories = [
    { Category: 'Medication', Order: 0 },
    { Category: 'Non-Medication', Order: 1 },
    { Category: 'Food', Order: 2 },
  ];

  public readonly compositeMetrics = ['Blood glucose test', 'Total Cholesterol test'];

  public readonly surgeryStatus = {
    default: 'SurgeryUnderwent',
  };

  // Changing the order of these codes will require refactoring components that reference this array.
  covidCodes = ['U07.1', 'U07.2'];
  allTrackingConditionCodes = [...this.covidCodes];
  availableHomeTabs = [
    {
      Index: 0,
      Screen: 'schedule',
    },
    {
      Index: 1,
      Screen: 'tracker',
    },
    {
      Index: 2,
      Screen: 'history',
    },
    {
      Index: 3,
      Screen: 'path',
    },
    {
      Index: 4,
      Screen: 'documents',
    },
    {
      Index: 5,
      Screen: 'whatsapp',
    },
  ];
  availableConsultationScreens = [
    {
      Index: 0,
      Screens: ['notes'],
    },
    {
      Index: 1,
      Screens: ['symptoms'],
    },
    {
      Index: 2,
      Screens: ['examination'],
    },
    {
      Index: 3,
      Screens: ['templates', 'diagnosis', 'medicine', 'procedure', 'consumable', 'review'],
    },
    {
      Index: 4,
      Screens: ['plan'],
    },
  ];

  readonly defaultVaccinationByAgeGroup = [
    {
      ageGroup: 'Birth',
      ageForMoment: {
        unit: 0,
        unitType: 'days',
      },
      vaccinations: ['BCG', 'OPV (0)'],
    },
    {
      ageGroup: '6 Weeks',
      ageForMoment: {
        unit: 6,
        unitType: 'weeks',
      },
      vaccinations: ['OPV (1)', 'RV (1)', 'DTaP-IPV-Hib-HBV (1)', 'PCV (1)'],
    },
    {
      ageGroup: '10 Weeks',
      ageForMoment: {
        unit: 10,
        unitType: 'weeks',
      },
      vaccinations: ['DTaP-IPV-Hib-HBV (2)', 'RV (2)', 'PCV (2)'],
    },
    {
      ageGroup: '14 Weeks',
      ageForMoment: {
        unit: 14,
        unitType: 'weeks',
      },
      vaccinations: ['RV (3)', 'DTaP-IPV-Hib-HBV (3)', 'PCV (3)'],
    },
    {
      ageGroup: '6 Months',
      ageForMoment: {
        unit: 6,
        unitType: 'months',
      },
      vaccinations: ['MR (1)'],
    },
    {
      ageGroup: '9 Months',
      ageForMoment: {
        unit: 9,
        unitType: 'months',
      },
      vaccinations: ['PCV (4)'],
    },
    {
      ageGroup: '12 Months',
      ageForMoment: {
        unit: 12,
        unitType: 'months',
      },
      vaccinations: ['MR (2)'],
    },
    { ageGroup: '18 Months', ageForMoment: { unit: 18, unitType: 'months' }, vaccinations: ['DTaP-IPV-Hib-HBV (4)'] },
    {
      ageGroup: '24 Months',
      ageForMoment: {
        unit: 24,
        unitType: 'months',
      },
      vaccinations: ['Optional booster for Hep A/ Varicella'],
    },
    {
      ageGroup: '6 Years',
      ageForMoment: {
        unit: 6,
        unitType: 'years',
      },
      vaccinations: ['TdaP (1)', 'MR (2)'],
    },
    {
      ageGroup: '9 Years',
      ageForMoment: {
        unit: 9,
        unitType: 'years',
      },
      vaccinations: ['HPV (1)'],
    },
    {
      ageGroup: '12 Years',
      ageForMoment: {
        unit: 12,
        unitType: 'years',
      },
      vaccinations: ['TdaP (2)'],
    },
  ];

  readonly availableCustomVaccines = [
    'Tdap',
    'Vaxigrip',
    'Influvac',
    'Hep A',
    'Hep B',
    'Hep A/B',
    'PPSV23',
    'PCV13',
    'MenB',
    'Typhoid',
    'Rabivax',
    'Verorab',
    'ADACEL',
    'HPV Gardasil 4',
    'HPV Gardasil 9',
    'Other',
  ].sort();

  readonly defaultDevelopmentalScreen = [
    {
      ageGroup: 'Birth',
      ageForMoment: {
        unit: 0,
        unitType: 'days',
      },
      milestones: [{ type: 'textbox', label: 'Notes', id: 'birth-notes' }],
    },
    {
      ageGroup: '6 Weeks',
      ageForMoment: {
        unit: 6,
        unitType: 'weeks',
      },
      milestones: [{ type: 'textbox', label: 'Notes', id: '6-weeks-notes' }],
    },
    {
      ageGroup: '10 Weeks',
      ageForMoment: {
        unit: 10,
        unitType: 'weeks',
      },
      milestones: [{ type: 'textbox', label: 'Notes', id: '10-weeks-notes' }],
    },
    {
      ageGroup: '14 Weeks',
      ageForMoment: {
        unit: 14,
        unitType: 'weeks',
      },
      hasSelectAll: true,
      milestones: [
        { type: 'checkbox', label: 'Startles to loud sounds', category: 'listening', id: '14-weeks-startles' },
        { type: 'checkbox', label: 'Follows face or close objects with eyes', category: 'visual', id: '14-weeks-follows' },
        { type: 'checkbox', label: 'Smiles at people', category: 'cognition', id: '14-weeks-smiles' },
        { type: 'checkbox', label: 'Holds head upright when held against shoulder', category: 'motor', id: '14-weeks-head-upright' },
        { type: 'checkbox', label: 'Hands are open most of the time', category: 'motor', id: '14-weeks-hands-open' },
      ],
    },
    {
      ageGroup: '6 Months',
      ageForMoment: {
        unit: 6,
        unitType: 'months',
      },
      hasSelectAll: true,
      milestones: [
        { type: 'checkbox', label: 'Moves eyes or head in direction of sounds', category: 'listening', id: '6-months-moves-eyes' },
        { type: 'checkbox', label: 'Responds by making sounds when talked to', category: 'listening', id: '6-months-responds-sounds' },
        { type: 'checkbox', label: 'Eyes move well together (no squint)', category: 'visual', id: '6-months-eyes-move-well' },
        { type: 'checkbox', label: 'Recognises familiar faces', category: 'visual', id: '6-months-recognises-faces' },
        { type: 'checkbox', label: 'Looks at own hands', category: 'visual', id: '6-months-looks-at-hands' },
        { type: 'checkbox', label: 'Laughs aloud', category: 'cognition', id: '6-months-laughs-aloud' },
        {
          type: 'checkbox',
          label: 'Uses different cries/sounds to show hunger, tiredness, discomfort',
          category: 'cognition',
          id: '6-months-uses-cries',
        },
        { type: 'checkbox', label: 'Grasps toy in each hand', category: 'motor', id: '6-months-grasps-toy' },
        { type: 'checkbox', label: 'Lifts head when lying on tummy', category: 'motor', id: '6-months-lifts-head' },
      ],
    },
    {
      ageGroup: '9 Months',
      ageForMoment: {
        unit: 9,
        unitType: 'months',
      },
      hasSelectAll: true,
      milestones: [
        { type: 'checkbox', label: 'Babbles ("ma-ma", "da-da")', category: 'listening', id: '9-months-babbles' },
        { type: 'checkbox', label: 'Turns when called', category: 'listening', id: '9-months-turns-called' },
        { type: 'checkbox', label: 'Eyes focus on far objects', category: 'visual', id: '9-months-eyes-focus' },
        { type: 'checkbox', label: 'Throws, bangs toys/objects', category: 'cognition', id: '9-months-throws-bangs' },
        {
          type: 'checkbox',
          label: 'Reacts when caregiver leaves, calms when she/he returns',
          category: 'cognition',
          id: '9-months-reacts-caregiver',
        },
        { type: 'checkbox', label: 'Sits without support', category: 'motor', id: '9-months-sits-without-support' },
        { type: 'checkbox', label: 'Moves objects from hand to hand', category: 'motor', id: '9-months-moves-objects' },
      ],
    },
    {
      ageGroup: '12 Months',
      ageForMoment: {
        unit: 12,
        unitType: 'months',
      },
      hasSelectAll: true,
      milestones: [
        {
          type: 'checkbox',
          label: 'Uses simple gestures (e.g. lifts arms to be picked up)',
          category: 'listening',
          id: '12-months-uses-simple-gestures',
        },
        {
          type: 'checkbox',
          label: 'Has one meaningful word (e.g. "mama", "dada")',
          category: 'listening',
          id: '12-months-has-one-meaningful-word',
        },
        { type: 'checkbox', label: 'Imitates different speech sounds', category: 'listening', id: '12-months-imitates-speech-sounds' },
        { type: 'checkbox', label: 'Understands "no"', category: 'listening', id: '12-months-understands-no' },
        { type: 'checkbox', label: 'Looks for toys/objects that disappear', category: 'visual', id: '12-months-looks-for-toys' },
        {
          type: 'checkbox',
          label: 'Looks closely at toys/objects and pictures',
          category: 'visual',
          id: '12-months-looks-closely-at-toys',
        },
        { type: 'checkbox', label: 'Imitates gestures (e.g. clapping hands)', category: 'visual', id: '12-months-imitates-gestures' },
        { type: 'checkbox', label: 'Stands with support', category: 'motor', id: '12-months-stands-with-support' },
        {
          type: 'checkbox',
          label: 'Picks up small objects with thumb and index finger',
          category: 'motor',
          id: '12-months-picks-up-small-objects',
        },
      ],
    },
    {
      ageGroup: '18 Months',
      ageForMoment: {
        unit: 18,
        unitType: 'months',
      },
      hasSelectAll: true,
      milestones: [
        {
          type: 'checkbox',
          label: 'Understands names of at least 2 common objects (e.g. cup)',
          category: 'listening',
          id: '18-months-understands-names',
        },
        { type: 'checkbox', label: 'Uses at least 3 words other than names', category: 'listening', id: '18-months-uses-at-least-3-words' },
        { type: 'checkbox', label: 'Looks at small things and pictures', category: 'visual', id: '18-months-looks-at-small-things' },
        {
          type: 'checkbox',
          label: 'Follows simple commands (e.g. "come here")',
          category: 'cognition',
          id: '18-months-follows-simple-commands',
        },
        { type: 'checkbox', label: 'Walks alone', category: 'motor', id: '18-months-walks-alone' },
        { type: 'checkbox', label: 'Uses fingers to feed', category: 'motor', id: '18-months-uses-fingers-to-feed' },
      ],
    },
    {
      ageGroup: '3 years',
      ageForMoment: {
        unit: 3,
        unitType: 'years',
      },
      hasSelectAll: true,
      milestones: [
        {
          type: 'checkbox',
          label: 'Speaks in simple 3-word sentences',
          category: 'listening',
          id: '3-years-speaks-in-simple-3-word-sentences',
        },
        {
          type: 'checkbox',
          label: 'Sees small shapes clearly at a distance (across the room)',
          category: 'visual',
          id: '3-years-sees-small-shapes-clearly',
        },
        { type: 'checkbox', label: 'Plays with other children/adults', category: 'cognition', id: '3-years-plays-with-other-children' },
        { type: 'checkbox', label: 'Uses pretend play (e.g. feeds a doll)', category: 'cognition', id: '3-years-uses-pretend-play' },
        { type: 'checkbox', label: 'Runs well', category: 'motor', id: '3-years-runs-well' },
        { type: 'checkbox', label: 'Eats on own', category: 'motor', id: '3-years-eats-on-own' },
      ],
    },
    {
      ageGroup: '5-6 years',
      ageForMoment: {
        isRange: true,
        from: { unit: 5, unitType: 'years' },
        to: { unit: 6, unitType: 'years' },
      },
      hasSelectAll: true,
      milestones: [
        { type: 'checkbox', label: 'Speaks in full sentences', id: '5-6-years-speaks-in-full-sentences', category: 'listening' },
        {
          type: 'checkbox',
          label: 'Caregiver understands child`s speech',
          category: 'listening',
          id: '5-6-years-caregiver-understands-speech',
        },
        {
          type: 'checkbox',
          label: 'No reported/observed vision problems (use "E" chart if available)',
          category: 'visual',
          id: '5-6-years-no-vision-problems',
        },
        { type: 'checkbox', label: 'Interacts with children and adults', category: 'cognition', id: '5-6-years-interacts-with-children' },
        {
          type: 'checkbox',
          label: 'Understands multiple commands (e.g. "go to the kitchen and bring me your plate")',
          category: 'cognition',
          id: '5-6-years-understands-multiple-commands',
        },
        { type: 'checkbox', label: 'Hops on one foot', category: 'motor', id: '5-6-years-hops-on-one-foot' },
        {
          type: 'checkbox',
          label: 'Holds pencil/stick with fingers at top/middle to draw',
          category: 'motor',
          id: '5-6-years-holds-pencil-stick',
        },
        { type: 'checkbox', label: 'Dresses self', category: 'motor', id: '5-6-years-dresses-self' },
      ],
    },
  ];

  metricPattern = '^(\\+{1,4}|\\d+)((\\.|\\,)\\d{1,4})?$';

  readonly symptomQuestions$ = this.httpClient
    .get<SymptomQuestion[]>(`/assets/reference-data/symptoms/symptom-questions.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly symptoms$ = this.merakiLookupService.getVisitReasons().pipe(shareReplay(1));

  //TODO: Firestore migration moved this into single document. Find a way to do this in a better
  readonly symptomsConfig$: Observable<SymptomConfiguration[]> = this.merakiLookupService.getVisitReasons().pipe(shareReplay(1));

  readonly questions$ = this.httpClient
    .get<QuestionBase[]>(`/assets/reference-data/symptoms/questions.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  /**************** MEDICAL AIDS & HEALTHID ****************/

  readonly medicalAids$ = this.authQuery.currentTenantId$.pipe(
    switchMap(currentTenantId => this.patientClient.getAllMedicalAids(currentTenantId)),
    map(medicalAids =>
      _.chain(medicalAids)
        .orderBy(s => s.Name)
        .value()
    ),
    shareReplay(1)
  );

  private readonly flattenedMedicalAids$ = this.medicalAids$.pipe(
    map(medicalAids =>
      medicalAids.flatMap(scheme =>
        scheme.Plans.flatMap(plan =>
          plan.Options.flatMap(option => ({
            SchemeName: scheme.Name,
            SchemeCode: scheme.SchemeCode,

            PlanName: plan.Name,
            PlanCode: plan.PlanCode,

            OptionName: option.Name,
            OptionCode: option.OptionCode,
            RoutingCode: option.RoutingCode,
            PVEnabled: option.RoutingCode,
          }))
        )
      )
    ),
    shareReplay(1)
  );

  readonly healthIDSSupportedSchemes$ = this.httpClient
    .get<string[]>(`/assets/reference-data/healthid-schemes.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly tarrifMedicalTypeCodes$ = this.httpClient
    .get<{ SpecialityCode: number; Description: string; Type: string }[]>(`/assets/reference-data/medical_type.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  public readonly dosageNumbersOptions = [0.25, 1, 0.5, 2, 3, 4, 5, 10, 20];
  public readonly siUnitsOptions = ['μg / mcg', 'mg', 'g', 'ml'];

  public readonly routeOptions = [
    { description: 'orally', value: 'PO' },
    { description: 'per the rectum', value: 'PR' },
    { description: 'per the vagina', value: 'PV' },
    { description: 'under tongue', value: 'SL' },
    { description: 'into muscle', value: 'IM' },
    { description: 'under skin', value: 'SC' },
  ];

  public readonly routeOptionsByProductFormDescription = [
    {
      productFormDescription: 'eye',
      options: [
        { description: 'Right eye', value: 'OD' },
        { description: 'Left eye', value: 'OS' },
        { description: 'Both eyes', value: 'OU' },
      ],
    },
  ];

  public readonly routeOptionsPerCategory = [
    {
      category: 'Administration',
      options: [
        { description: 'PO (orally)', value: 'PO' },
        { description: 'PR (per rectum)', value: 'PR' },
        { description: 'PV (per vagina)', value: 'PV' },
        { description: 'SL (under tongue)', value: 'SL' },
      ],
    },
    {
      category: 'Injection',
      options: [
        { description: 'IM (into muscle)', value: 'IM' },
        { description: 'SC (under skin)', value: 'SC' },
      ],
    },
    {
      category: 'Ear',
      options: [
        { description: 'Left ear', value: 'Left ear' },
        { description: 'Right ear', value: 'Right ear' },
        { description: 'Both ears', value: 'Both ears' },
      ],
    },
    {
      category: 'Eye',
      options: [
        { description: 'Left eye', value: 'Left eye' },
        { description: 'Right eye', value: 'Right eye' },
        { description: 'Both eyes', value: 'Both eyes' },
      ],
    },
    {
      category: 'Nose',
      options: [{ description: 'In nose', value: 'In nose' }],
    },
  ];

  public readonly frequencyOptions = [
    {
      order: 0,
      category: 'Per day',
      periodType: 'day',
      options: [
        { display: 'Q1D (1 x per day)', FrequencyUnits: 1, PeriodUnits: 1, PeriodType: 'day' },
        { display: 'BD (2 x per day)', FrequencyUnits: 2, PeriodUnits: 1, PeriodType: 'day' },
        { display: 'TDS (3 x per day)', FrequencyUnits: 3, PeriodUnits: 1, PeriodType: 'day' },
        { display: 'QID (4 x per day)', FrequencyUnits: 4, PeriodUnits: 1, PeriodType: 'day' },
      ],
    },
    {
      order: 2,
      category: 'Hours',
      periodType: 'hour',
      options: [
        { display: 'Q4h (every 4 hours)', FrequencyUnits: 1, PeriodUnits: 4, PeriodType: 'hour' },
        { display: 'Q6H (every 6 hours)', FrequencyUnits: 1, PeriodUnits: 6, PeriodType: 'hour' },
        { display: 'Q8H (every 8 hours)', FrequencyUnits: 1, PeriodUnits: 8, PeriodType: 'hour' },
        { display: 'Q12H (every 12 hours)', FrequencyUnits: 1, PeriodUnits: 12, PeriodType: 'hour' },
      ],
    },
    {
      order: 1,
      category: 'Weeks',
      periodType: 'week',
      options: [
        { display: 'Once weekly', FrequencyUnits: 1, PeriodUnits: 1, PeriodType: 'week' },
        { display: 'Twice weekly', FrequencyUnits: 2, PeriodUnits: 1, PeriodType: 'week' },
        { display: 'Thrice weekly', FrequencyUnits: 3, PeriodUnits: 1, PeriodType: 'week' },
      ],
    },
    {
      order: 3,
      category: 'Month',
      periodType: 'month',
      options: [{ display: 'Monthly', FrequencyUnits: 1, PeriodUnits: 1, PeriodType: 'month' }],
    },
  ].sort((a, b) => a.order - b.order);

  public readonly durationUnitOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  public readonly durationUnitTypeOptions = ['days', 'weeks', 'months'];

  public readonly repeatOptions = [0, 1, 2, 3, 4, 5, 6];

  public readonly additionalOptionsFull = [
    { label: 'A.M.', meaning: 'morning' },
    { label: 'P.M.', meaning: 'evening' },
    { label: 'ac', meaning: 'before meals' },
    { label: 'pc', meaning: 'after meals' },
    { label: 'mane', meaning: 'in the morning' },
    { label: 'ap', meaning: 'before dinner' },
    { label: 'BT', meaning: 'bedtime' },
    { label: 'achs', meaning: 'before meals and at bedtime' },
    { label: 'qn', meaning: 'nightly or at bedtime' },
    { label: 'noct', meaning: 'in the night' },
    { label: 'PRN', meaning: 'PRN' },
    { label: 'QOD', meaning: 'every other day' },
    { label: 's.o.s', meaning: 'if necessary' },
    { label: 'STAT', meaning: 'STAT' },
    { label: 'Allow generics', meaning: 'Allow generics' },
    { label: 'No generics', meaning: 'No generics' },
    { label: 'q.p.m', meaning: 'in the afternoon' },
  ];

  public readonly additionalOptionsFullGenerics = [
    { label: 'A.M.', meaning: 'morning' },
    { label: 'P.M.', meaning: 'evening' },
    { label: 'ac', meaning: 'before meals' },
    { label: 'pc', meaning: 'after meals' },
    { label: 'mane', meaning: 'in the morning' },
    { label: 'ap', meaning: 'before dinner' },
    { label: 'BT', meaning: 'bedtime' },
    { label: 'achs', meaning: 'before meals and at bedtime' },
    { label: 'qn', meaning: 'nightly or at bedtime' },
    { label: 'noct', meaning: 'in the night' },
    { label: 'PRN', meaning: 'PRN' },
    { label: 'QOD', meaning: 'every other day' },
    { label: 's.o.s', meaning: 'if necessary' },
    { label: 'STAT', meaning: 'STAT' },
    { label: 'Allow for substitutes', meaning: 'Allow for substitutes' },
    { label: 'No substitutes', meaning: 'No substitutes' },
    { label: 'q.p.m', meaning: 'in the afternoon' },
  ];

  /**************** OTHER ****************/

  readonly languages$ = this.httpClient.get<Language[]>(`/assets/reference-data/languages.json?v=${VERSION.version}`).pipe(shareReplay(1));

  readonly maritalStatuses$ = this.httpClient
    .get<string[]>(`/assets/reference-data/marital-statuses.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly surgeries$ = this.httpClient.get<ISurgery[]>(`/assets/reference-data/surgeries.json?v=${VERSION.version}`).pipe(shareReplay(1));

  readonly clinicalMetrics$ = this.httpClient
    .get<IMetricSchema[]>(`/assets/reference-data/metrics.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly lifestyle$ = this.httpClient
    .get<QuestionBase[]>(`/assets/reference-data/lifestyle.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly familyHistory$ = this.httpClient
    .get<QuestionBase[]>(`/assets/reference-data/family-history.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  //getVisitReasonQuestions

  readonly womenHealthQuestions$ = this.merakiLookupService.getVisitReasonQuestions('womens-health').pipe(
    map(data => data.map(m => m.Question)),
    shareReplay(1)
  );

  // this.httpClient
  //   .get<QuestionBase[]>(`/assets/reference-data/women-health.json?v=${VERSION.version}`)
  //   .pipe(shareReplay(1));

  public PlaceOfServiceCodes: Array<PlaceOfService> = [
    { name: 'Consulting room', code: '11' },
    { name: 'Inpatient Hospital', code: '21' },
    { name: 'Day Clinic/hospital', code: '24' },
    { name: 'Telemedicine', code: '02' },
    { name: 'Home', code: '12' },
  ];
  countries$ = this.merakiLookupService.getCountries().pipe(
    shareReplay(1),
    map(countries => countries.sort((a, b) => a.Name.localeCompare(b.Name)))
  );

  public readonly planOutcomes = [
    { Title: 'Path request', Type: PlanOutcomeTypes.PathRequest },
    { Title: 'Follow-up visit', Type: PlanOutcomeTypes.FollowUp },
    { Title: 'Referred', Type: PlanOutcomeTypes.Referred },
    { Title: 'Ultrasound', Type: PlanOutcomeTypes.Ultrasound },
    { Title: 'Radiology', Type: PlanOutcomeTypes.Radiology },
    { Title: 'Admitted to hospital', Type: PlanOutcomeTypes.AdmitToHospital },
    { Title: 'Discharged from hospital', Type: PlanOutcomeTypes.DischargeFromHospital },
    { Title: 'Prescription sent', Type: PlanOutcomeTypes.PrescriptionSent },
  ] as EncounterPlanOutcomeVo[];

  public planOutcomesToTaskTypes = {
    [PlanOutcomeTypes.AdmitToHospital]: TaskType.AdmitToHospital,
    [PlanOutcomeTypes.FollowUp]: TaskType.FollowUpVisit,
    [PlanOutcomeTypes.PathRequest]: TaskType.PathologyResult,
    [PlanOutcomeTypes.PrescriptionSent]: TaskType.Prescriptions,
    [PlanOutcomeTypes.Radiology]: TaskType.Radiology,
    [PlanOutcomeTypes.Referred]: TaskType.ReferralLetter,
  };

  readonly myPatientFilesCategories$ = this.httpClient
    .get<MyPatientFilesCategories>(`/assets/reference-data/mypatientfiles-categories.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly proceduresNotTelehealtSpecific = ['0130', 'VCONM', 'VCOMS', 'VCOMSC'];

  readonly peadsMeasurementMetricNames = ['Weight', 'Height', 'Head-circumference'];

  readonly randomColors$ = this.httpClient
    .get<UiColor[]>(`/assets/reference-data/random-colors.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  /**
   * Metrics read from metrics.json that must be displayed in the General Screening table by default
   */
  readonly screeningMetrics$: Observable<IMetricSchemaItem[]> = this.httpClient
    .get<IMetricSchema[]>(`/assets/reference-data/metrics.json?v=${VERSION.version}`)
    .pipe(
      map(schemas => {
        const Items = schemas
          .flatMap(s => s.items)
          .filter(item => item.usage?.some(usage => usage === MetricUsages.all || usage === MetricUsages.screening));

        return Items as IMetricSchemaItem[];
      }),
      shareReplay(1)
    );

  /**
   * Metrics read from metrics.json that can be added to the General Screening table but are not displayed by default.
   */
  readonly screeningCommonMetrics$: Observable<IMetricSchemaItem[]> = this.httpClient
    .get<IMetricSchema[]>(`/assets/reference-data/metrics.json?v=${VERSION.version}`)
    .pipe(
      map(schemas => {
        const Items = schemas.flatMap(s => s.items).filter(item => item.usage?.some(usage => usage === MetricUsages.commonScreening));

        return Items as IMetricSchemaItem[];
      }),
      shareReplay(1)
    );

  readonly allergens$ = this.httpClient
    .get<AllergenVo[]>(`/assets/reference-data/allergens.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  readonly genericMedicines$ = this.httpClient
    .get<GenericMedicine>(`/assets/reference-data/generic-medicine.json?v=${VERSION.version}`)
    .pipe(shareReplay(1));

  public readonly SpecialityCodesForEnhancedHospitalConsultReasons = [
    '032', //Paediatrician
  ];

  // note: keep in mind to update server side if add new values
  public readonly SpecialPlaceholders = [
    { text: 'Patient title', value: '#PatientTitle' },
    { text: 'Patient fullname', value: '#PatientFullname' },
    { text: 'Patient first name', value: '#PatientFirstname' },
    { text: 'Patient surname', value: '#PatientSurname' },
    { text: 'Patient ID number', value: '#PatientIdentityNo' },
    { text: 'Patient email address', value: '#PatientEmailAddress' },
    { text: 'Patient mobile number', value: '#PatientMobileNumber' },
    { text: 'Membership number', value: '#MembershipNumber' },
    { text: 'Account Number', value: '#AccountNo' },
    { text: 'Medical aid name', value: '#MedicalAidName' },
    { text: 'Medical aid plan', value: '#MedicalAidPlan' },
    { text: 'Medical aid option', value: '#MedicalAidOption' },
    { text: 'Patient allergies', value: '#PatientAllergies' },
    { text: 'ICD10 code(s)', value: '#ICD10Codes' },
    { text: 'Practice name', value: '#PracticeName' },
    { text: 'Practice office number', value: '#PracticeOfficeNumber' },
    { text: 'Practice mobile number', value: '#PracticeMobileNumber' },
    { text: 'Provider name', value: '#ProviderName' },
    { text: 'Provider signature', value: '#ProviderSignature' },
    { text: 'Report date', value: '#ReportDate' },
    { text: '	Patient DOB', value: '#PatientDOB' },
    { text: '	File number', value: '#FileNumber' },
    { text: '	Occupation', value: '#Occupation' },
    { text: 'Employer', value: '#Employer' },
    { text: '	Address 1', value: '#Address1' },
    { text: '	Address 2', value: '#Address2' },
    { text: '	Address 3', value: '#Address3' },
    { text: '	Postal code', value: '#PostalCode' },
  ];

  /**************** METHODS ****************/

  constructor(
    private patientClient: PatientClient,
    private authQuery: AuthQuery,
    private httpClient: HttpClient,
    private algoliaRestService: AlgoliaRestService,
    private merakiLookupService: MerakiLookupService
  ) {}

  getSymptomQuestions(symptomId) {
    return this.httpClient
      .get<SymptomQuestion[]>(`/assets/reference-data/symptoms/${symptomId}-symptom-questions.json?v=${VERSION.version}`)
      .pipe(shareReplay(1));
  }

  getQuestionsBySymptom(symptomId) {
    return this.httpClient
      .get<QuestionBase[]>(`/assets/reference-data/symptoms/${symptomId}-questions.json?v=${VERSION.version}`)
      .pipe(shareReplay(1));
  }

  getQuestions() {
    return this.httpClient.get<QuestionBase[]>(`/assets/reference-data/symptoms/questions.json?v=${VERSION.version}`).pipe(shareReplay(1));
  }

  getCountries() {
    return this.merakiLookupService.getCountries();
  }

  getPaedsData(metric: string, gender: string, GrowthGroup: 'adolescent' | 'infant') {
    return this.httpClient
      .get<any[]>(`/assets/reference-data/paeds/${GrowthGroup}/${gender.toLowerCase()}/${metric.toLowerCase()}.json?v=${VERSION.version}`)
      .pipe(map(data => this.paedsToDefault(data)));
  }

  paedsToDefault(data: any[]): ChartDefaultData[] {
    if ('Month' in data[0]) {
      return data.map(
        point =>
          ({
            ...point,
            XValue: point.Month,
          }) as ChartDefaultData
      );
    }

    if ('Length' in data[0]) {
      return data.map(
        point =>
          ({
            ...point,
            XValue: point.Length,
          }) as ChartDefaultData
      );
    }

    if ('Day' in data[0]) {
      const result: ChartDefaultData[] = [];
      for (let index = 0; index < 60; index++) {
        const dataDay = data[index * 30];
        result.push({
          ...dataDay,
          XValue: index,
        } as ChartDefaultData);
      }
      return result;
    }
    return [];
  }

  getMedicineCategories() {
    return this.algoliaRestService.getAlgoliaMedicineCategories().pipe(shareReplay(1));
  }

  limitType(limit: LimitAvailable) {
    switch (limit.Limit) {
      case 'GP_VISIT15':
        return 'GP visit/s';

      case 'AFT_VISIT':
        return 'After-hours consultation/s';

      case 'PATH_VISIT':
        return 'Pathology visit/s';

      case 'RAD_VISIT':
        return 'Radiology visit/s';

      case 'GP_VISIT':
        return 'Out-of-network visit/s';

      case 'PHAR_VISIT':
        return 'Pharmacy visit/s';

      default:
        return limit.LimitDescription;
    }
  }

  filterPatientCriteriaByAge(f: QuestionBase, age: number): boolean {
    return !f.Filter || (f.Filter?.Older && f.Filter?.Older < age) || (f.Filter?.Younger && f.Filter?.Younger > age);
  }
}
